import React, { useContext, useRef, useState } from "react";
import {
  StyledFormContainer,
  StyledGeneratorContainer,
  StyledMainContainer,
  StyledPdfPage,
} from "../../styles/global";
import GenerateUrlQr from "./methods/GenerateUrlQr";
import { StyledGeneratorOptions, StyledQRContainer } from "./styled";
import QRCode from "qrcode.react";
import GenerateWhatsAppQr from "./methods/GenerateWhatsAppQr";
import Button from "../../components/Button";
import GenerateTextQr from "./methods/GenerateTextQr";
import GenerateSmsQr from "./methods/GenerateSmsQr";
import GeneratePhoneQr from "./methods/GeneratePhoneQr";
import GenerateEmailQr from "./methods/GenerateEmailQr";
import GenerateWiFiQr from "./methods/GenerateWiFiQr";
import GenerateLocationQr from "./methods/GenerateLocationQr";
import Accordion from "../../components/Accordion";
import {
  convertImageFromBase64,
  downloadQR,
  handleQrFormValidations,
  isPaidUser,
} from "../../helper/functions";
import Range from "../../components/Range";
import FileUploader from "../../components/FileUploader";
import Dialog from "../../components/Dialog";
import { useReactToPrint } from "react-to-print";
import PdfPage from "../../components/PdfPage";
import { QrType } from "../../types/types";
import { LoaderContext } from "../../context/LoaderContext";
import api from "../../api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import InputField from "../../components/InputField";
import { FaFilePdf } from "react-icons/fa";
import { MdFileDownload, MdPhoto } from "react-icons/md";
import { AiOutlineFileJpg } from "react-icons/ai";
import { SiJpeg } from "react-icons/si";
import { ImSvg } from "react-icons/im";
import { GoPlus } from "react-icons/go";
import GenerateUPIQr from "./methods/GenerateUPIQr";
import { availableQrTypes } from "../../utils/globals";
import Header from "../../components/Header";
import Checkbox from "../../components/Checkbox";
import ColorPicker from "../../components/ColorPicker";
import { useToken } from "../../helper/hooks";

interface Props {}

const GenerateQR: React.FC<Props> = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { showLoader } = useContext(LoaderContext);
  const { setToken } = useToken();

  const [value, setValue] = useState("");
  const [qrName, setQrName] = useState("");
  const [QrType, setQrType] = useState<QrType>("url");
  const [brandImage, setBrandImage] = useState("");
  const [logoSize, setLogoSize] = useState(40);
  const [brandImageName, setBrandImageName] = useState("");
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<any>(null);
  const [nameError, setNameError] = useState("");
  const [excavateLogo, setExcavateLogo] = useState(false);
  const [qrColors, setQrColors] = useState({
    fgColor: "#000000",
    bgColor: "#ffffff",
  });

  const navigate = useNavigate();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const errorOcurred = () => {
    let _errorFlag = false;
    let { hasError, error } = handleQrFormValidations(QrType, value);
    if (hasError) {
      setErrors(error);
      _errorFlag = true;
    }
    if (!qrName) {
      setNameError("Please enter a name for the QR");
      _errorFlag = true;
    }
    if (_errorFlag) {
      toast.error("Please fill all the required fields");
    }
    return _errorFlag;
  };

  const handleQRSave = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    if (errorOcurred()) {
      return;
    }

    showLoader({
      show: true,
    });
    let qrImg = await convertImageFromBase64("image/png", "qrvv.png");
    let formData = new FormData();
    formData.append("qr", qrImg);
    formData.append("type", QrType);
    formData.append("name", qrName);
    formData.append("fgColor", qrColors.fgColor);
    formData.append("bgColor", qrColors.bgColor);
    formData.append("excavate", JSON.stringify(excavateLogo));
    formData.append("brandLogoName", brandImageName);

    api
      .SaveQRCode(formData)
      .then((res) => {
        if (res.status === 201) {
          toast.success(res.data.message);
          navigate("/dashboard");
        }
      })
      .catch((err) => {
        if (err.response?.data?.data?.token) {
          setToken(err.response?.data?.data?.token);
        }
        toast.error(err.response?.data?.message || "Something went wrong");
      })
      .finally(() => {
        showLoader({
          show: false,
        });
      });
  };

  const renderForm = () => {
    switch (QrType) {
      case "text":
        return <GenerateTextQr error={errors} setValue={setValue} />;
      case "url":
        return <GenerateUrlQr error={errors} setValue={setValue} />;
      case "whatsapp":
        return <GenerateWhatsAppQr errors={errors} setValue={setValue} />;
      case "sms":
        return <GenerateSmsQr errors={errors} setValue={setValue} />;
      case "phone":
        return <GeneratePhoneQr error={errors} setValue={setValue} />;
      case "email":
        return <GenerateEmailQr errors={errors} setValue={setValue} />;
      case "location":
        return <GenerateLocationQr errors={errors} setValue={setValue} />;
      case "wifi":
        return <GenerateWiFiQr errors={errors} setValue={setValue} />;
      case "upi":
        return <GenerateUPIQr errors={errors} setValue={setValue} />;
      default:
        setQrType("text");
        return <GenerateTextQr error={errors} setValue={setValue} />;
    }
  };

  return (
    <>
      <Header title="WhatQR" />
      <StyledPdfPage>
        <PdfPage
          title={` Scan QR code to get information`}
          ref={componentRef}
          QRComponent={
            <QRCode
              value={value}
              size={330}
              renderAs={"svg"}
              level={"H"}
              includeMargin
              fgColor={qrColors.fgColor}
              bgColor={qrColors.bgColor}
              imageSettings={{
                src: brandImage,
                x: undefined,
                y: undefined,
                height: logoSize,
                width: logoSize,
                excavate: excavateLogo,
              }}
            />
          }
        />
      </StyledPdfPage>

      <Dialog
        title="Download QR"
        onClose={() => {
          setShowDownloadOptions(false);
        }}
        open={showDownloadOptions}
      >
        <Button
          icon={<MdPhoto />}
          label="Download PNG"
          onClick={async () => {
            downloadQR("image/png", QrType + "-qr.png", "qr-img");
          }}
        />
        <Button
          icon={<AiOutlineFileJpg />}
          label="Download JPG"
          onClick={() => {
            downloadQR("image/jpg", QrType + "-qr.jpg", "qr-img");
          }}
        />
        <Button
          icon={<SiJpeg />}
          label="Download JPEG"
          onClick={() => {
            downloadQR("image/jpeg", QrType + "-qr.jpeg", "qr-img");
          }}
        />

        <Button
          icon={<ImSvg />}
          label="Download SVG"
          onClick={() => {
            downloadQR("image/svg+xml", QrType + "-qr.svg", "qr-img");
          }}
        />
      </Dialog>
      <StyledMainContainer>
        <StyledGeneratorOptions>
          <h1>
            Generate {availableQrTypes.find(({ id }) => id === QrType)?.label}{" "}
            QR code{" "}
          </h1>

          <div>
            {availableQrTypes.map((item) => (
              <Button
                key={item.id}
                icon={item.icon}
                active={QrType === item.id}
                style={{
                  margin: "0.5rem",
                }}
                onClick={() => {
                  setErrors(null);
                  setNameError("");
                  setQrType(item.id);
                  setValue("");
                }}
                label={item.ignoreLabel ? "" : item.label}
              />
            ))}
          </div>
        </StyledGeneratorOptions>

        <StyledGeneratorContainer>
          <StyledFormContainer>
            {isAuthenticated && (
              <InputField
                error={nameError}
                type="text"
                value={qrName}
                onChange={(e) => {
                  setQrName(e.target.value);
                }}
                label="QR Name"
                name="qrname"
                placeholder="Enter name for this QR code"
              />
            )}
            {renderForm()}
            <Accordion
              title="Colors"
              body={
                <>
                  {/* <input
                    type={"color"}
                    value={qrColors.fgColor}
                    onChange={(e) => {
                      setQrColors({
                        ...qrColors,
                        fgColor: e.target.value,
                      });
                    }}
                  /> */}
                  <ColorPicker
                    style={{
                      margin: "5px 0",
                    }}
                    id="fgColor"
                    label="Select foreground color"
                    value={qrColors.fgColor}
                    onChange={(color) => {
                      setQrColors({
                        ...qrColors,
                        fgColor: color,
                      });
                    }}
                  />
                  <ColorPicker
                    style={{
                      margin: "5px 0",
                    }}
                    id="bgColor"
                    label="Select background color"
                    value={qrColors.bgColor}
                    onChange={(color) => {
                      setQrColors({
                        ...qrColors,
                        bgColor: color,
                      });
                    }}
                  />
                </>
              }
            />
            <Accordion
              title="Branding"
              disabled={!isPaidUser(user).isPaid}
              helperText={
                !isPaidUser(user).isPaid
                  ? "Upgrade to paid plan to use this feature."
                  : ""
              }
              body={
                <>
                  <FileUploader
                    setValue={(value: string) => setBrandImage(value)}
                    fileName={brandImageName}
                    setFileName={(value: string) => setBrandImageName(value)}
                    name="logo"
                  />
                  <Range
                    value={logoSize}
                    onChange={(e) => {
                      setLogoSize(parseInt(e.target.value));
                    }}
                    title="Logo size"
                    name="range"
                    max={50}
                  />
                  <Checkbox
                    label="Excavate"
                    checked={excavateLogo}
                    onChange={(checked) => {
                      setExcavateLogo(checked);
                    }}
                    messageOnHover="Checking this option will make a void space around your logo. This may cause some scanning issues, so please scan and test before moving forward."
                    style={{
                      margin: "16px 0",
                    }}
                  />
                </>
              }
            />

            <Button
              style={{ width: "100%", marginTop: "24px" }}
              icon={<GoPlus />}
              label={isAuthenticated ? "Create" : "Login to save it"}
              active={isAuthenticated}
              onClick={(e) => {
                if (isAuthenticated) {
                  handleQRSave(e);
                } else {
                  navigate("/login");
                }
              }}
            />
          </StyledFormContainer>
          <StyledQRContainer>
            <QRCode
              id={"qr-img"}
              value={value}
              size={350}
              level="H"
              renderAs="svg"
              fgColor={qrColors.fgColor}
              bgColor={qrColors.bgColor}
              includeMargin={true}
              imageSettings={{
                src: brandImage,
                height: logoSize,
                width: logoSize,
                excavate: excavateLogo,
              }}
            />
            <div className="qr-actions">
              <Button
                icon={<FaFilePdf />}
                label="Print QR"
                onClick={() => {
                  handlePrint();
                }}
              />
              <Button
                active
                icon={<MdFileDownload />}
                label="Download QR"
                onClick={() => {
                  setShowDownloadOptions(true);
                }}
              />
            </div>
          </StyledQRContainer>
        </StyledGeneratorContainer>
      </StyledMainContainer>
    </>
  );
};

export default GenerateQR;
